import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/api";
import { useLang } from "@/i18n";
import { CheckCircle2, XCircle } from "lucide-react";
import { trackPurchase } from "@/analytics/metaPixel";

export default function PaymentResult({ success }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [isShop, setIsShop] = useState(false);

  useEffect(() => {
    if (!success) return;
    if (params.get("provider") === "paypal") {
      const token = params.get("token");
      if (!token) { setStatus("timeout"); return; }
      api.post("/payments/paypal/capture", { order_id: token })
        .then((r) => {
          setIsShop(((r.data || {}).lookup_key || "").startsWith("shop:"));
          setStatus("paid");
          if (!sessionStorage.getItem(`pixel-purchase-pp-${token}`)) {
            sessionStorage.setItem(`pixel-purchase-pp-${token}`, "1");
            trackPurchase(((r.data || {}).amount || 0) / 100);
          }
        })
        .catch(() => setStatus("timeout"));
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setIsShop((data.lookup_key || "").startsWith("shop:"));
          setStatus("paid");
          if (!sessionStorage.getItem(`pixel-purchase-${sessionId}`)) {
            sessionStorage.setItem(`pixel-purchase-${sessionId}`, "1");
            trackPurchase((data.amount || 0) / 100);
          }
          return;
        }
      } catch (e) { /* retry */ }
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [success, sessionId]);

  if (!success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="kc-card p-10 max-w-md text-center" data-testid="payment-cancel-page">
          <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-black uppercase mb-4">{t("pay.cancel")}</h1>
          <Link data-testid="payment-back-link" to="/app/packages" className="kc-btn-secondary inline-block">{t("pay.backHome")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="kc-card p-10 max-w-md text-center" data-testid="payment-success-page">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-black uppercase mb-2">{t("pay.success")}</h1>
        <p className="text-muted-foreground mb-6" data-testid="payment-activation-status">
          {status === "paid" ? (isShop ? t("pay.orderConfirmed") : t("pay.activated")) : t("pay.activating")}
        </p>
        <Link data-testid="payment-back-link" to="/app" className="kc-btn-primary inline-block">{t("pay.backHome")}</Link>
      </div>
    </div>
  );
}
